import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Tooltip,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {
  getAuth,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { ReactComponent as GoogleLogo } from "../assets/googlesignin.svg";

/* ────────────────────────────────────────────────────────────── */

const withTimeout = (promise, ms, label = "operation") =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), ms)
    ),
  ]);

/* ────────────────────────────────────────────────────────────── */

function InAppBrowserBanner({ show }) {
  const [copied, setCopied] = useState(false);

  if (!show) return null;

  const ua = (navigator.userAgent || "").toLowerCase();
  const isAndroid = /android/i.test(ua);
  const isIOS = /iphone|ipad|ipod/i.test(ua);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch (_) {
      const tmp = document.createElement("input");
      tmp.value = window.location.href;
      document.body.appendChild(tmp);
      tmp.select();
      document.execCommand("copy");
      document.body.removeChild(tmp);
    } finally {
      setCopied(true);
      window.clearTimeout(copyLink._t);
      copyLink._t = window.setTimeout(() => setCopied(false), 1200);
    }
  };

  const openInChromeAndroid = () => {
    const url = window.location.href;
    const intent = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(
      url
    )};end`;
    window.location.href = intent;
  };

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: "#000",
        color: "#fff",
        px: 2,
        py: 1.5,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderTop: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, lineHeight: 1.1, mb: 0.25 }}
        >
          Open in Browser
        </Typography>
        <Typography
          variant="body2"
          sx={{ opacity: 0.9, lineHeight: 1.25, pr: 1 }}
        >
          {isIOS
            ? 'Google sign-in needs Safari. Tap the "…" menu → “Open in Safari”.'
            : "Google sign-in needs Chrome. Tap Open in Chrome or copy the link."}
        </Typography>
      </Box>

      <Button
        onClick={copyLink}
        size="small"
        variant={copied ? "contained" : "outlined"}
        sx={{
          bgcolor: copied ? "#2e7d32" : "transparent",
          color: "#fff",
          borderColor: "rgba(255,255,255,0.35)",
          "&:hover": {
            borderColor: "#fff",
            backgroundColor: copied ? "#2e7d32" : "rgba(255,255,255,0.06)",
          },
          textTransform: "none",
          fontWeight: copied ? 700 : 600,
        }}
      >
        {copied ? "Copied ✓" : "Copy link"}
      </Button>

      {isAndroid && (
        <Button
          onClick={openInChromeAndroid}
          size="small"
          variant="contained"
          sx={{
            bgcolor: "#fff",
            color: "#000",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { bgcolor: "#f2f2f2" },
          }}
        >
          Open in Chrome
        </Button>
      )}
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────── */

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [inApp, setInApp] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const ua = (navigator.userAgent || "").toLowerCase();
    const hay = ua + " " + (document.referrer || "").toLowerCase();
    const detected =
      /(instagram|fb_iab|fbav|facebook|messenger|line|micromessenger|wechat|weibo|qq|snapchat|pinterest|tiktok|bytedance|musical\.ly|lemon8|ttwebview|twitter)/i.test(
        hay
      );
    setInApp(detected);
  }, []);

  // --- APPLE SIGN IN ---
  // Same flow as SignUp: we send the user to Apple. Apple comes back to
  // /auth/callback/apple with ?code=..., and AppleCallback finishes login.
  const APPLE_REDIRECT_URI = "https://elevateevent.co/auth/callback/apple";
  const APPLE_CLIENT_ID = "co.elevateevent.web";
  const APPLE_SCOPE = ""; // leave empty -- we are not requesting "name email" yet
  const APPLE_RESPONSE_TYPE = "code";
  const APPLE_RESPONSE_MODE = "query";

  function randomState() {
    return Math.random().toString(36).slice(2);
  }

  const handleAppleStart = () => {
  setLoading(true);
    // we're OK letting Apple run in in-app browsers, unlike Google
    const state = randomState();
    const url = new URL("https://appleid.apple.com/auth/authorize");

    url.searchParams.set("response_type", APPLE_RESPONSE_TYPE);
    url.searchParams.set("response_mode", APPLE_RESPONSE_MODE);
    url.searchParams.set("client_id", APPLE_CLIENT_ID);
    url.searchParams.set("redirect_uri", APPLE_REDIRECT_URI);

    if (APPLE_SCOPE) {
      url.searchParams.set("scope", APPLE_SCOPE);
    }

    url.searchParams.set("state", state);

    sessionStorage.setItem("apple_oauth_state", state);

    window.location.href = url.toString();
  };

  // Google OAuth (popup) — disabled inside in-app browsers
  const handleGoogleSignIn = async () => {
    if (inApp) return;

    setLoading(true);

    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      const { user } = await signInWithPopup(auth, provider);

      const db = getFirestore();

      let canGoDashboard = false;

      try {
        const [subs, pays, eventsSnap] = await withTimeout(
          Promise.all([
            getDocs(collection(db, `users/${user.uid}/subscriptions`)),
            getDocs(collection(db, `users/${user.uid}/payments`)),
            getDocs(collection(db, `users/${user.uid}/events`)),
          ]),
          2500,
          "Sign-in account check"
        );

        const hasSub = !subs.empty;
        const hasPaid = pays.docs.some((d) => d.data().status === "succeeded");
        const hasEvent = !eventsSnap.empty;

        canGoDashboard = hasEvent || hasSub || hasPaid;
      } catch (lookupErr) {
        console.warn("Sign-in account check skipped:", lookupErr);

        // If the lookup is slow, do not make sign-in feel broken.
        // Existing users are safer going to dashboard.
        canGoDashboard = true;
      }

      navigate(canGoDashboard ? "/eventdashboard" : "/create-event", {
        replace: true,
      });
    } catch (err) {
      console.error("Google sign-in error:", err);
      alert("Google sign-in error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      const isPasswordUser = user?.providerData?.some(
        (p) => p.providerId === "password"
      );

      if (isPasswordUser && !user.emailVerified) {
        alert("Please verify your email before signing in.");
        setLoading(false);
        return;
      }

      const db = getFirestore();
      const subscriptionsRef = collection(db, `users/${user.uid}/subscriptions`);
      const paymentsRef = collection(db, `users/${user.uid}/payments`);
      const eventsRef = collection(db, `users/${user.uid}/events`);

      let subscriptionsSnapshot;
      let paymentsSnapshot;
      let eventsSnapshot;

      try {
        [subscriptionsSnapshot, paymentsSnapshot, eventsSnapshot] =
          await withTimeout(
            Promise.all([
              getDocs(subscriptionsRef),
              getDocs(paymentsRef),
              getDocs(eventsRef),
            ]),
            2500,
            "Email sign-in account check"
          );
      } catch (lookupErr) {
        console.warn("Email sign-in account check skipped:", lookupErr);

        // If account checks are slow, do not leave user buffering.
        navigate("/eventdashboard", { replace: true });
        return;
      }

      const hasActiveSubscription = !subscriptionsSnapshot.empty;

      const hasSuccessfulPayment = paymentsSnapshot.docs.some((doc) => {
        const data = doc.data();
        return data.status === "succeeded";
      });

      const hasEvent = !eventsSnapshot.empty;

      // special “just verified” flow:
      let cameFromVerify = false;

      try {
        if (localStorage.getItem("postVerifyBypass") === "1") {
          cameFromVerify = true;
          localStorage.removeItem("postVerifyBypass");
        }
      } catch (_) {}

      // If they JUST verified and still have no event or payment,
      // send them to create their first event.
      if (
        cameFromVerify &&
        !hasEvent &&
        !hasActiveSubscription &&
        !hasSuccessfulPayment
      ) {
        navigate("/create-event", { replace: true });
        return;
      }

      const canGoDashboard =
        hasEvent || hasActiveSubscription || hasSuccessfulPayment;

      navigate(canGoDashboard ? "/eventdashboard" : "/create-event", {
        replace: true,
      });
    } catch (error) {
      console.error("Error signing in:", error);
      alert("Error signing in: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Container
        maxWidth="sm"
        sx={{
          marginTop: { xs: "80px", md: "100px" },
          paddingBottom: "20px",
        }}
      >
        <Paper elevation={3} sx={{ p: 2.5, mt: "50px" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography variant="h4" component="h1" gutterBottom>
              Sign In
            </Typography>

            <form onSubmit={handleSignIn} style={{ width: "100%" }}>
              <Grid container spacing={2}>
                {/* Google button (disabled in in-app browsers) */}
                <Grid item xs={12}>
                  <Box textAlign="center" mb={1}>
                    <Box
                      onClick={handleGoogleSignIn}
                      aria-label="Sign in with Google"
                      sx={{
                        display: "inline-block",
                        cursor: inApp ? "not-allowed" : "pointer",
                        opacity: inApp ? 0.4 : 1,
                        transition: "opacity .2s ease",
                        pointerEvents: inApp ? "none" : "auto",
                      }}
                    >
                      <GoogleLogo />
                    </Box>
                  </Box>
                </Grid>

                {/* Apple sign in */}
                <Grid item xs={12}>
                  <Box textAlign="center" mb={1}>
                    <Button
                      type="button"
                      onClick={handleAppleStart}
                      aria-label="Sign in with Apple"
                      sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        borderRadius: "999px",
                        px: 2.5,
                        py: 1.2,
                        fontWeight: 600,
                        textTransform: "none",
                        minWidth: 260,
                        fontSize: "0.9rem",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
                        "&:hover": {
                          backgroundColor: "#111",
                          boxShadow: "0 6px 14px rgba(0,0,0,0.5)",
                        },
                      }}
                      disabled={loading}
                    >
                      <span
                        style={{
                          fontSize: "1rem",
                          marginRight: "8px",
                        }}
                      >
                        
                      </span>
                      Continue with Apple
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider>or</Divider>
                </Grid>

                {/* Email field */}
                <Grid item xs={12}>
                  <Tooltip title="Enter your registered email address" arrow>
                    <TextField
                      label="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      autoFocus
                      InputProps={{
                        endAdornment: (
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </Tooltip>
                </Grid>

                {/* Password field */}
                <Grid item xs={12}>
                  <Tooltip title="Enter your password" arrow>
                    <TextField
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      fullWidth
                      required
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <IconButton>
                            <InfoIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </Tooltip>
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      background:
                        "linear-gradient(90deg, #2A73E8 0%, #1A5BBE 100%)",
                      color: "#fff",
                      p: "12px 16px",
                      fontWeight: "bold",
                      fontSize: "16px",
                      textTransform: "uppercase",
                      borderRadius: "8px",
                      "&:hover": {
                        background:
                          "linear-gradient(90deg, #1A5BBE 0%, #12459A 100%)",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>
      </Container>

      {/* Minimal bottom banner only when inside in-app browsers */}
      <InAppBrowserBanner show={inApp} />
    </>
  );
};

export default SignIn;